import React, { useState, useEffect, useCallback } from 'react';
import { blogAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Plus, Pencil, Trash2, X, Save, Eye, EyeOff } from 'lucide-react';

const AdminBlogs = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [form, setForm] = useState({
    title: '', excerpt: '', content: '', category: 'Treatment', tags: '', featuredImage: '', published: true
  });
  const [error, setError] = useState(null);

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await blogAPI.getPosts(1, 50);
      setPosts(data.posts || []);
    } catch { setError('Failed to load blog posts'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const resetForm = () => {
    setForm({ title: '', excerpt: '', content: '', category: 'Treatment', tags: '', featuredImage: '', published: true });
    setEditingPost(null);
    setShowForm(false);
  };

  const handleEdit = (post) => {
    setForm({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      tags: (post.tags || []).join(', '),
      featuredImage: post.featuredImage || '',
      published: post.published,
    });
    setEditingPost(post);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      featuredImage: form.featuredImage || null,
    };
    try {
      if (editingPost) {
        await blogAPI.updatePost(editingPost.id, payload);
      } else {
        await blogAPI.createPost(payload);
      }
      resetForm();
      fetchPosts();
    } catch (err) { alert('Error saving blog post'); }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await blogAPI.deletePost(postId);
      fetchPosts();
    } catch (err) { alert('Error deleting blog post'); }
  };

  const categories = ['Treatment', 'Prevention', 'Care', 'Research', 'Conference', 'Community', 'News'];

  return (
    <div data-testid="admin-blogs">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Blog Posts</h1>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-teal-700 hover:bg-teal-600" data-testid="add-blog-button">
          <Plus className="w-4 h-4 mr-2" /> New Post
        </Button>
      </div>

      {showForm && (
        <Card className="bg-slate-900 border-slate-800 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">{editingPost ? 'Edit Post' : 'New Post'}</h2>
              <button onClick={resetForm} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Title *</Label>
                  <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="bg-slate-800 border-slate-700 text-white" required data-testid="blog-title-input" />
                </div>
                <div>
                  <Label className="text-slate-300">Category</Label>
                  <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full h-10 rounded-md border bg-slate-800 border-slate-700 text-white px-3 text-sm" data-testid="blog-category-select">
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Excerpt *</Label>
                <textarea value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} className="w-full min-h-[80px] rounded-md border bg-slate-800 border-slate-700 text-white px-3 py-2 text-sm" required data-testid="blog-excerpt-input" />
              </div>
              <div>
                <Label className="text-slate-300">Content * (Supports markdown-style text. Paste image URLs directly in content.)</Label>
                <textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} className="w-full min-h-[250px] rounded-md border bg-slate-800 border-slate-700 text-white px-3 py-2 text-sm font-mono" required data-testid="blog-content-input" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Featured Image URL</Label>
                  <Input value={form.featuredImage} onChange={e => setForm({...form, featuredImage: e.target.value})} placeholder="https://example.com/image.jpg" className="bg-slate-800 border-slate-700 text-white" data-testid="blog-image-input" />
                </div>
                <div>
                  <Label className="text-slate-300">Tags (comma separated)</Label>
                  <Input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} placeholder="Cancer, Treatment, Research" className="bg-slate-800 border-slate-700 text-white" data-testid="blog-tags-input" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.published} onChange={e => setForm({...form, published: e.target.checked})} id="published" className="rounded" />
                <label htmlFor="published" className="text-slate-300 text-sm">Published</label>
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="bg-teal-700 hover:bg-teal-600" data-testid="blog-save-button">
                  <Save className="w-4 h-4 mr-2" /> {editingPost ? 'Update' : 'Create'} Post
                </Button>
                <Button type="button" variant="ghost" onClick={resetForm} className="text-slate-400">Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-slate-400">Loading...</div>
      ) : posts.length === 0 ? (
        <div className="text-center text-slate-400 py-12">No blog posts yet. Create your first post!</div>
      ) : (
        <div className="space-y-3">
          {posts.map(post => (
            <Card key={post.id} className="bg-slate-900 border-slate-800" data-testid={`blog-item-${post.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-white font-medium truncate">{post.title}</h3>
                    <Badge variant="outline" className="text-xs border-slate-700 text-slate-400">{post.category}</Badge>
                    {post.published ? (
                      <Badge className="bg-green-500/10 text-green-400 text-xs"><Eye className="w-3 h-3 mr-1" />Live</Badge>
                    ) : (
                      <Badge className="bg-yellow-500/10 text-yellow-400 text-xs"><EyeOff className="w-3 h-3 mr-1" />Draft</Badge>
                    )}
                  </div>
                  <p className="text-slate-500 text-sm truncate">{post.excerpt}</p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <Button size="sm" variant="ghost" onClick={() => handleEdit(post)} className="text-slate-400 hover:text-white" data-testid={`edit-blog-${post.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(post.id)} className="text-slate-400 hover:text-red-400" data-testid={`delete-blog-${post.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBlogs;
